/**
 * Phase 6 Session 4 — scenario replay helper. Mirrored from
 * `shared/src/util/scenario-replay.ts` per the 5.1 lock (web does NOT
 * import from `@hearth/shared`). Edit both files together.
 */

import {
  COL_INDEX_2026,
  colMultiplier,
  type CityCode,
} from "../data/cost-of-living.js";
import type { HistoricalRealYearWithCape } from "../data/historical-returns.js";
import type {
  FireAssumptions,
  ScenarioDelta,
} from "../api-modules/types.js";
import { fireNumbers, timeToFI, type FireNumbersResult } from "./fire.js";
import { buildRetirementPlan } from "./retirement-plan.js";
import { socialSecurityNpvAtRetirement } from "./social-security.js";
import {
  historicalReturnStats,
  monteCarloSimulation,
  trinityBackTest,
  type MonteCarloResult,
  type TrinityBackTestResult,
} from "./stress-test.js";

export interface ScenarioReplayInput {
  readonly household_assumptions: FireAssumptions | null;
  readonly scenario: { readonly delta: ScenarioDelta };
  readonly helpers_input: {
    readonly current_net_worth_cents: number;
    readonly monthly_net_savings_cents: number;
    readonly annual_spend_cents: number;
    readonly today: string;
    readonly trinity_returns: readonly HistoricalRealYearWithCape[];
    /** Tax on retirement withdrawals, from the household's account mix. */
    readonly effective_tax_rate_pct?: number;
  };
}

export interface ScenarioReplayResult {
  readonly merged_assumptions: FireAssumptions;
  readonly effective_annual_spend_cents: number;
  readonly effective_monthly_savings_cents: number;
  readonly effective_starting_portfolio_cents: number;
  readonly fire_target_cents: number;
  readonly fire_numbers: FireNumbersResult;
  readonly social_security_offset_cents: number;
  readonly time_to_fi_months: number | null;
  readonly time_to_fi_date: string | null;
  readonly trinity_success_pct: number;
  readonly monte_carlo_success_pct: number;
  readonly monte_carlo_p50_end_cents: number;
}

const DEFAULT_WR = 4;
const DEFAULT_RETURN = 5;
const DEFAULT_RETIREMENT_AGE = 65;
const DEFAULT_EQUITY_PCT = 60;
const DEFAULT_BOND_PCT = 40;

function mergeAssumptions(
  base: FireAssumptions | null,
  delta: ScenarioDelta,
): FireAssumptions {
  const merged: FireAssumptions = { ...(base ?? {}) };
  if (delta.withdrawal_rate_pct !== undefined) {
    merged.withdrawal_rate_pct = delta.withdrawal_rate_pct;
  }
  if (delta.expected_return_rate_pct !== undefined) {
    merged.expected_return_rate_pct = delta.expected_return_rate_pct;
  }
  if (delta.withdrawal_strategy !== undefined) {
    merged.withdrawal_strategy = delta.withdrawal_strategy;
  }
  if (delta.spending_smile_enabled !== undefined) {
    merged.spending_smile_enabled = delta.spending_smile_enabled;
  }
  if (delta.spending_smile_curve !== undefined) {
    merged.spending_smile_curve = delta.spending_smile_curve;
  }
  if (delta.bond_tent_enabled !== undefined) {
    merged.bond_tent_enabled = delta.bond_tent_enabled;
  }
  if (delta.annual_spend_override_cents !== undefined) {
    merged.annual_spend_override_cents = delta.annual_spend_override_cents;
  }
  if (delta.current_city_code !== undefined) {
    merged.current_city_code = delta.current_city_code;
  }
  return merged;
}

function isKnownCity(code: string | undefined): code is CityCode {
  return code !== undefined && code in COL_INDEX_2026;
}

export function replayScenario(input: ScenarioReplayInput): ScenarioReplayResult {
  const { scenario, helpers_input } = input;
  const merged_assumptions = mergeAssumptions(
    input.household_assumptions,
    scenario.delta,
  );

  let effective_annual_spend_cents =
    merged_assumptions.annual_spend_override_cents ??
    helpers_input.annual_spend_cents;

  const fromCity = merged_assumptions.current_city_code;
  const toCity = scenario.delta.relocate_to_city_code;
  if (toCity && isKnownCity(fromCity) && isKnownCity(toCity)) {
    const mult = colMultiplier(fromCity, toCity);
    effective_annual_spend_cents = Math.round(
      effective_annual_spend_cents * mult,
    );
  }

  if (scenario.delta.annual_spend_delta_cents !== undefined) {
    effective_annual_spend_cents = Math.max(
      0,
      effective_annual_spend_cents + scenario.delta.annual_spend_delta_cents,
    );
  }

  const effective_monthly_savings_cents =
    scenario.delta.monthly_net_savings_override_cents !== undefined
      ? scenario.delta.monthly_net_savings_override_cents
      : helpers_input.monthly_net_savings_cents;

  const effective_starting_portfolio_cents =
    helpers_input.current_net_worth_cents +
    (scenario.delta.portfolio_one_time_delta_cents ?? 0);

  const fire_numbers = fireNumbers({
    annual_spend_cents: effective_annual_spend_cents,
    withdrawal_rate_pct:
      merged_assumptions.withdrawal_rate_pct ?? DEFAULT_WR,
    ...(merged_assumptions.lean_spend_multiplier !== undefined && {
      lean_spend_multiplier: merged_assumptions.lean_spend_multiplier,
    }),
    ...(merged_assumptions.fat_spend_multiplier !== undefined && {
      fat_spend_multiplier: merged_assumptions.fat_spend_multiplier,
    }),
  });

  const retirement_target_age =
    merged_assumptions.retirement_target_age ?? DEFAULT_RETIREMENT_AGE;
  const social_security_offset_cents =
    merged_assumptions.social_security_pia_cents !== undefined &&
    merged_assumptions.social_security_pia_cents > 0
      ? socialSecurityNpvAtRetirement({
          pia_at_fra_cents: merged_assumptions.social_security_pia_cents,
          claim_age:
            merged_assumptions.social_security_claim_age ?? retirement_target_age,
          retirement_target_age,
          expected_return_rate_pct:
            merged_assumptions.expected_return_rate_pct ?? DEFAULT_RETURN,
        })
      : 0;

  const fire_target_cents = Math.max(
    0,
    fire_numbers.regular_cents - social_security_offset_cents,
  );

  const time = timeToFI({
    current_net_worth_cents: effective_starting_portfolio_cents,
    monthly_net_savings_cents: effective_monthly_savings_cents,
    annual_return_rate_pct:
      merged_assumptions.expected_return_rate_pct ?? DEFAULT_RETURN,
    fire_target_cents,
    today: helpers_input.today,
  });

  // === the household's own retirement plan ==================================
  // Trinity and Monte Carlo must start from what this household will
  // actually have. Starting from the FIRE target would give every
  // household the same answer, whatever its savings or spending.
  const plan = buildRetirementPlan({
    fi_assets_cents: effective_starting_portfolio_cents,
    monthly_net_savings_cents: effective_monthly_savings_cents,
    annual_return_rate_pct:
      merged_assumptions.expected_return_rate_pct ?? DEFAULT_RETURN,
    current_year: Number(helpers_input.today.slice(0, 4)),
    retirement_target_age,
    birth_year_primary: merged_assumptions.birth_year_primary,
    birth_year_spouse: merged_assumptions.birth_year_spouse,
    social_security_pia_cents: merged_assumptions.social_security_pia_cents,
    social_security_claim_age: merged_assumptions.social_security_claim_age,
    social_security_pia_spouse_cents:
      merged_assumptions.social_security_pia_spouse_cents,
    social_security_claim_age_spouse:
      merged_assumptions.social_security_claim_age_spouse,
    pre_medicare_healthcare_annual_cents:
      merged_assumptions.pre_medicare_healthcare_annual_cents,
  });
  const taxRatePct = helpers_input.effective_tax_rate_pct ?? 0;
  const stats = historicalReturnStats(helpers_input.trinity_returns);

  const trinity = trinityBackTest({
    portfolio_value_cents: plan.projected_portfolio_cents,
    annual_spend_cents: effective_annual_spend_cents,
    equity_pct: DEFAULT_EQUITY_PCT,
    bond_pct: DEFAULT_BOND_PCT,
    horizon_years: plan.horizon_years,
    returns: helpers_input.trinity_returns,
    target_withdrawal_rate_pct:
      merged_assumptions.withdrawal_rate_pct ?? DEFAULT_WR,
    annual_adjustments_cents: plan.annual_adjustments_cents,
    effective_tax_rate_pct: taxRatePct,
  });

  const monte = monteCarloSimulation({
    portfolio_value_cents: plan.projected_portfolio_cents,
    annual_spend_cents: effective_annual_spend_cents,
    expected_return_pct: stats.equity_mean_pct,
    equity_stddev_pct: stats.equity_stddev_pct,
    bond_mean_pct: stats.bond_mean_pct,
    bond_stddev_pct: stats.bond_stddev_pct,
    equity_pct: DEFAULT_EQUITY_PCT,
    bond_pct: DEFAULT_BOND_PCT,
    horizon_years: plan.horizon_years,
    target_withdrawal_rate_pct:
      merged_assumptions.withdrawal_rate_pct ?? DEFAULT_WR,
    annual_adjustments_cents: plan.annual_adjustments_cents,
    effective_tax_rate_pct: taxRatePct,
  });

  return {
    merged_assumptions,
    effective_annual_spend_cents,
    effective_monthly_savings_cents,
    effective_starting_portfolio_cents,
    fire_target_cents,
    fire_numbers,
    social_security_offset_cents,
    time_to_fi_months: time.months_to_fi,
    time_to_fi_date: time.fi_date,
    trinity_success_pct: trinity.success_pct,
    monte_carlo_success_pct: monte.success_pct,
    monte_carlo_p50_end_cents: monte.final_percentiles.p50,
  };
}

export type { TrinityBackTestResult, MonteCarloResult };
